import type { Token, TokenType } from './types'

// Lightweight Lucene-like tokenizer used by the parser
export class Lexer {
  private cursorIndex = 0
  constructor(private readonly input: string) {}

  // Map of single-character punctuation/operators to token types
  static readonly SINGLE_CHAR_TOKEN_MAP: Record<string, TokenType> = {
    ':': 'COLON',
    '(': 'LPAREN',
    ')': 'RPAREN',
    ',': 'COMMA',
    '*': 'ASTERISK',
    '-': 'MINUS',
    '>': 'MORE_THAN',
    '<': 'LESS_THAN',
  }

  // Scan an identifier allowing backslash escapes and limited punctuation.
  // Starts scanning at `startIndex` and returns the unescaped text and the
  // index just after the last consumed character.
  private scanIdentifier(
    source: string,
    inputLength: number,
    startIndex: number,
  ): { text: string; endIndex: number } {
    let lookaheadIndex = startIndex
    let buf = ''
    while (lookaheadIndex < inputLength) {
      const ch = source[lookaheadIndex]
      if (ch === '\\' && lookaheadIndex + 1 < inputLength) {
        buf += source[lookaheadIndex + 1]
        lookaheadIndex += 2
        continue
      }
      if (/[^A-Za-z0-9_.-]/.test(ch)) break
      buf += ch
      lookaheadIndex++
    }
    return { text: buf, endIndex: lookaheadIndex }
  }

  private skipWhitespace(source: string, inputLength: number) {
    const slice = source.slice(this.cursorIndex, inputLength)
    const firstWhitespaceIndex = slice.search(/\S/)

    this.cursorIndex =
      this.cursorIndex +
      (firstWhitespaceIndex === -1 ? slice.length : firstWhitespaceIndex)
  }

  private trySingleCharToken(source: string, pos: number) {
    const ch = source[this.cursorIndex]
    const singleCharToken = Lexer.SINGLE_CHAR_TOKEN_MAP[ch]
    if (singleCharToken) {
      this.cursorIndex++
      return { type: singleCharToken, pos }
    }
    return null
  }

  private tryOperatorToken(
    source: string,
  ): { type: TokenType; pos: number } | null {
    const character = source[this.cursorIndex]
    const pos = this.cursorIndex
    if (character === '>') {
      if (source[this.cursorIndex + 1] === '=') {
        this.cursorIndex += 2
        return { type: 'MORE_THAN_EQUAL', pos }
      }
      return null
    }

    if (character === '<') {
      if (source[this.cursorIndex + 1] === '=') {
        this.cursorIndex += 2
        return { type: 'LESS_THAN_EQUAL', pos }
      }
      return null
    }

    return null
  }

  private tryQuotedStringToken(
    source: string,
    inputLength: number,
    pos: number,
  ): Token | null {
    if (source[this.cursorIndex] !== '"') return null

    this.cursorIndex++
    let buf = ''
    let esc = false

    while (this.cursorIndex < inputLength) {
      const character = source[this.cursorIndex++]

      if (esc) {
        buf += character
        esc = false
        continue
      }

      if (character === '\\') {
        esc = true
        continue
      }

      if (character === '"') break
      buf += character
    }

    return { type: 'STRING', value: buf, pos }
  }

  private tryReadNumberToken(
    source: string,
    inputLength: number,
    pos: number,
  ): Token | null {
    const slice = source.slice(this.cursorIndex, inputLength)
    const match = /^\d+(?:\.\d*)?/.exec(slice)
    if (!match) return null

    const raw = match[0]
    this.cursorIndex += raw.length

    return { type: 'NUMBER', value: raw, pos }
  }

  private tryReadIdentifierOrKeyword(
    source: string,
    inputLength: number,
    pos: number,
  ): Token | null {
    const ch = source[this.cursorIndex]

    if (!/[A-Za-z_\\]/.test(ch)) return null

    const { text, endIndex } = this.scanIdentifier(
      source,
      inputLength,
      this.cursorIndex,
    )

    this.cursorIndex = endIndex
    const upper = text.toUpperCase()

    if (upper === 'AND' || upper === 'OR' || upper === 'NOT') {
      return { type: upper as TokenType, pos }
    }

    return { type: 'IDENTIFIER', value: text, pos }
  }

  private readFallbackIdentifier(source: string, pos: number): Token {
    // Read until unescaped whitespace; allow backslash-escaped whitespace and characters
    const input = source
    const len = input.length
    let i = this.cursorIndex
    let buf = ''
    while (i < len) {
      const ch = input[i]
      if (ch === '\\' && i + 1 < len) {
        // Preserve the backslash and the escaped char to keep raw form intact
        buf += ch + input[i + 1]
        i += 2
        continue
      }
      if (/\s/.test(ch)) break
      // Stop at structural characters that should be separate tokens
      if (ch === '(' || ch === ')') break
      buf += ch
      i++
    }
    this.cursorIndex = i
    return { type: 'IDENTIFIER', value: buf, pos }
  }

  next(): Token {
    const source = this.input
    const inputLength = source.length

    // Skip any leading whitespace
    this.skipWhitespace(source, inputLength)

    // End of input after trimming whitespace
    if (this.cursorIndex >= inputLength) {
      return { type: 'EOF', pos: this.cursorIndex }
    }

    const pos = this.cursorIndex
    return (
      this.tryOperatorToken(source) ??
      this.trySingleCharToken(source, pos) ??
      this.tryQuotedStringToken(source, inputLength, pos) ??
      this.tryReadNumberToken(source, inputLength, pos) ??
      this.tryReadIdentifierOrKeyword(source, inputLength, pos) ??
      this.readFallbackIdentifier(source, pos)
    )
  }
}
